import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Megaphone, CheckCircle2, Upload, IndianRupee, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { openRazorpay } from "@/lib/razorpay";

const CATEGORIES = ["सामान्य", "रोज़गार", "किराया / प्रॉपर्टी", "व्हीकल", "इलेक्ट्रॉनिक्स", "शिक्षा", "सेवाएँ", "अन्य"];

export default function PostAdPage() {
  const [config, setConfig] = useState(null);
  const [pkg, setPkg] = useState("featured");
  const [form, setForm] = useState({
    title: "", category: "सामान्य", description: "",
    phone: "", area: "", contact_name: "", email: "", image_url: "",
  });
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => { api.get("/payments/config").then((r) => setConfig(r.data)); }, []);

  const uploadImg = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload?watermark=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("फ़ोटो अपलोड हो गयी");
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.phone.trim() || !form.contact_name.trim()) {
      toast.error("सभी अनिवार्य फ़ील्ड भरें");
      return;
    }
    setProcessing(true);
    try {
      const { data } = await api.post("/classifieds/submit", { ...form, package_id: pkg });
      const res = await openRazorpay({
        order_id: data.order_id, amount: data.amount, currency: data.currency, key_id: data.key_id,
        description: `Classified — ${pkg.toUpperCase()}`,
        prefill: { name: form.contact_name, email: form.email, contact: form.phone },
      });
      if (res.ok) {
        setSuccess({ classified_id: data.classified_id, amount: data.amount / 100, pkg });
        toast.success("भुगतान सफल! विज्ञापन अनुमोदन के लिए भेज दिया गया है।");
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setProcessing(false); }
  };

  if (success) {
    return (
      <>
        <PublicHeader />
        <main className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="ad-success">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-600"/>
          </div>
          <h1 className="font-devnagri-heading text-4xl font-black text-[#0F172A]">भुगतान सफल! 🎉</h1>
          <p className="text-slate-600 mt-4 leading-relaxed">आपका विज्ञापन प्राप्त हो गया है। संपादक की समीक्षा के बाद यह प्रकाशित कर दिया जाएगा (24 घंटे में)।</p>
          <div className="mt-6 bg-slate-50 rounded-lg p-4 inline-block text-left">
            <div className="text-xs text-slate-500 uppercase tracking-widest">Reference</div>
            <div className="font-mono text-sm text-[#0F172A]">{success.classified_id}</div>
            <div className="text-xs text-slate-500 mt-2">पैकेज: <b>{success.pkg.toUpperCase()}</b> · राशि: <b>₹{success.amount}</b></div>
          </div>
          <div className="mt-8">
            <Button onClick={() => window.location.href = "/"} className="bg-[#0F172A] hover:bg-[#1E3A8A]">होम पर जाएँ</Button>
          </div>
        </main>
        <PublicFooter />
      </>
    );
  }

  const packages = config?.classified_packages || {};

  return (
    <>
      <PublicHeader />
      <section className="bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white">
        <div className="max-w-5xl mx-auto px-4 py-14">
          <div className="flex items-center gap-3 mb-3">
            <Megaphone className="w-5 h-5 text-amber-300"/>
            <span className="uppercase tracking-[0.28em] text-[11px] text-amber-300 font-black">Classified Ads · वर्गीकृत विज्ञापन</span>
          </div>
          <h1 className="font-devnagri-heading text-4xl md:text-5xl font-black">
            अपना विज्ञापन <span className="text-amber-300">यहाँ प्रकाशित करें</span>
          </h1>
          <p className="text-slate-300 mt-3 max-w-2xl">
            नौकरी, प्रॉपर्टी, व्हीकल, इलेक्ट्रॉनिक्स — INDIAN-NEWS-21 पर विज्ञापन दीजिये और हज़ारों पाठकों तक पहुँचिए।
          </p>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="post-ad-page">
        {/* Package picker */}
        <aside className="lg:col-span-2">
          <h3 className="font-bold text-[#0F172A] mb-3">पैकेज चुनें</h3>
          <div className="space-y-3">
            {Object.entries(packages).map(([id, p]) => (
              <button key={id} onClick={() => setPkg(id)}
                className={`w-full text-left p-4 rounded-lg border-2 transition ${
                  pkg === id ? "border-[#EA580C] bg-orange-50 shadow-md" : "border-slate-200 hover:border-slate-400 bg-white"
                }`}
                data-testid={`pkg-${id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-bold text-[#0F172A] flex items-center gap-2">
                    {p.label}
                    {id === "premium" && <Sparkles className="w-4 h-4 text-amber-500"/>}
                  </div>
                  <div className="text-2xl font-black text-emerald-700">₹{p.price}</div>
                </div>
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  {p.features?.map((f, i) => (
                    <li key={i} className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-500"/>{f}</li>
                  ))}
                </ul>
              </button>
            ))}
          </div>
        </aside>

        {/* Form */}
        <div className="lg:col-span-3 bg-white rounded-2xl shadow-lg border border-slate-200 p-6 md:p-8">
          <h3 className="font-bold text-[#0F172A] text-lg mb-4">विज्ञापन विवरण</h3>
          <div className="space-y-3">
            <Input placeholder="शीर्षक *" maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ad-title"/>
            <select className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ad-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <Textarea rows={5} maxLength={2000} placeholder="विवरण * — विज्ञापन के बारे में विस्तार से लिखें" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ad-description"/>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="संपर्क नाम *" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="ad-contact-name"/>
              <Input placeholder="फ़ोन *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ad-phone"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="क्षेत्र / शहर" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} data-testid="ad-area"/>
              <Input type="email" placeholder="ईमेल (वैकल्पिक)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ad-email"/>
            </div>
            <div>
              <label className="text-xs font-admin text-slate-600 mb-1 block">फ़ोटो अपलोड करें (वैकल्पिक)</label>
              <div className="flex items-center gap-3">
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-slate-300 hover:border-[#EA580C] text-sm">
                  <Upload className="w-4 h-4"/>{uploading ? "अपलोड हो रहा…" : "चुनें"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImg(e.target.files[0])} data-testid="ad-image-input"/>
                </label>
                {form.image_url && <img src={form.image_url} alt="" className="w-16 h-16 rounded object-cover"/>}
              </div>
            </div>
          </div>

          <Button onClick={submit} disabled={processing || !config?.enabled}
            className="w-full mt-6 bg-gradient-to-r from-[#0F172A] to-[#1E3A8A] hover:from-[#1E3A8A] hover:to-[#0F172A] text-white font-bold py-6 text-lg shadow-lg"
            data-testid="submit-ad-btn">
            <IndianRupee className="w-5 h-5 mr-1"/>
            {processing ? "प्रक्रिया हो रही…" : `${packages[pkg]?.price || 0} भुगतान करें और विज्ञापन दें`}
          </Button>
          <div className="text-xs text-slate-500 text-center mt-3">भुगतान सफल होने के बाद विज्ञापन संपादक की समीक्षा में जाएगा।</div>
        </div>
      </main>
      <PublicFooter />
    </>
  );
}
